import { StoreType } from '@tock/shared/utils/connect';
import { createSelector } from 'reselect';
import { dashboard } from 'types';
import { oc } from 'ts-optchain';
import protos from 'utils/protos';

const { DashboardPreferences } = protos;

const getActiveDashboardPreferences = ({
  app: { activeBusiness },
  dashboardPreferences,
}: Pick<StoreType, 'app' | 'dashboardPreferences'>) => {
  return activeBusiness ? dashboardPreferences[activeBusiness.id] : null;
};

export const getDashboardPreferencesSelector = createSelector(
  getActiveDashboardPreferences,
  (preferences): dashboard.DashboardPreferences => {
    const preferencesData = preferences?.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const defaultProto: any = DashboardPreferences.fromObject({});

    return {
      businessId: defaultProto.businessId,
      darkMode: defaultProto.darkMode,
      guestListSeparateTabs: defaultProto.guestListSeparateTabs,
      guestListCompactView: defaultProto.guestListCompactView,
      guestListShowTags: defaultProto.guestListShowTags,
      guestListShowNotes: defaultProto.guestListShowNotes,
      guestListShowTimeSlotHeaders: defaultProto.guestListShowTimeSlotHeaders,
      guestListShowUnassignedWarning: defaultProto.guestListShowUnassignedWarning,
      guestListShowLastnameFirstname: defaultProto.guestListShowLastnameFirstname,
      guestPanelAlwaysShowEgressNeeds: defaultProto.guestPanelAlwaysShowEgressNeeds,
      patronId: defaultProto.patronId,
      serviceTableShowPreassignments: defaultProto.serviceTableShowPreassignments,
      serviceTableShowTimers: defaultProto.serviceTableShowTimers,
      serviceServerCardShowPreassignmentCount: defaultProto.serviceServerCardShowPreassignmentCount,
      timelineDisplayHourlyTimeMarkers: defaultProto.timelineDisplayHourlyTimeMarkers,
      timelineTablesSortByPriority: defaultProto.timelineTablesSortByPriority,
      timelineTablesShowPriorityIndicators: defaultProto.timelineTablesShowPriorityIndicators,
      serviceEnableServerRotation: defaultProto.serviceEnableServerRotation,
      serviceServerRotationSortBy: defaultProto.serviceServerRotationSortBy,
      ...(preferencesData ? preferencesData : {}),
    };
  }
);

export const isDashboardPreferencesInitializedSelector = (state: StoreType): boolean =>
  oc(getActiveDashboardPreferences(state)).isInitialized(false);

export const isGuestListSeparateTabsSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).guestListSeparateTabs;

export const serviceTableShowTimersSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).serviceTableShowTimers;

export const guestListShowTagsSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).guestListShowTags;

export const guestListShowNotesSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).guestListShowNotes;

export const guestListShowUnassignedWarningSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).guestListShowUnassignedWarning;

export const guestListShowTimeSlotHeadersSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).guestListShowTimeSlotHeaders;

export const guestListShowLastnameFirstNameSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).guestListShowLastnameFirstname;

export const serviceServerCardShowPreassignmentCountSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).serviceServerCardShowPreassignmentCount;

export const showPreassignmentsSelector = (state: StoreType): boolean =>
  getDashboardPreferencesSelector(state).serviceTableShowPreassignments;

export const isHdModeEnabledSelector = (state: StoreType): boolean => {
  return getDashboardPreferencesSelector(state).guestListCompactView;
};

export const serviceEnableServerRotationSelector = (state: StoreType): boolean => {
  return getDashboardPreferencesSelector(state).serviceEnableServerRotation;
};
